/**
 * The minimal habit shape needed to follow `alsoCompletes` links.
 */
export interface HabitCompletionLinkNode {
   id: string
   kind: 'recurring' | 'one-off' | 'calendar'
   alsoCompletes: readonly string[]
}

/**
 * Resolves every habit completed by completing `habitId`, following
 * `alsoCompletes` links transitively (A → B → C completes all three).
 *
 * Linked habits only join the chain when they are recurring (paused ones are
 * stamped too but stay paused), and the chain only continues through habits
 * that joined it. Each habit is visited once, so mutual or circular links
 * terminate.
 *
 * @param habitId - The directly completed habit id.
 * @param habits - All of the user's habits.
 * @returns The completed ids, starting with `habitId`, in traversal order.
 */
export function resolveHabitCompletionChain(
   habitId: string,
   habits: readonly HabitCompletionLinkNode[],
) {
   const byId = new Map(habits.map(habit => [habit.id, habit]))
   const completedIds = [habitId]
   const visited = new Set(completedIds)
   // Array iteration also visits ids pushed during the loop (breadth-first).
   for (const completedId of completedIds) {
      for (const linkedId of byId.get(completedId)?.alsoCompletes ?? []) {
         const linked = byId.get(linkedId)
         if (visited.has(linkedId) || linked?.kind !== 'recurring') {
            continue
         }
         visited.add(linkedId)
         completedIds.push(linkedId)
      }
   }
   return completedIds
}
